import { IonBadge, IonButton, IonIcon, IonModal } from '@ionic/react';
import {
  addCircleOutline,
  caretBackOutline,
  closeOutline,
  cloudOfflineOutline,
  shareOutline,
  trashBinOutline,
} from 'ionicons/icons';
import React, { useContext, useState } from 'react';
import { ctxOrg, ctxProfile } from '../../../App';
import {
  ProductionSiteInspection,
  ProductionSiteInspectionReference,
} from '../../../InspectionModel';
import { ProductionSiteLocation } from '../../../Model';
import { userHasPermission } from '../../../PermissionsService';
import { buildProdSiteInspectionPath } from '../../../ServiceInspection';
import { InspectionType } from '../../../generated/openapi/core';
import { useGetFieldReportShares } from '../../../data/sharedFieldReport/sharedFieldReport.hooks';
import { firestore } from '../../../ConfigFirebase';
import { CSharedHistory } from '../../../components/CSharedHistory';
import { useLocationUtils } from '../../../hooks/useLocationUtils';
import { applicationStateObservable } from '../../../simpleObservable/observables';

interface LAProps {
  field: ProductionSiteLocation;
  isInSharingFlow: boolean;
  isInDeletionFlow: boolean;
  onShareStart: () => void;
  onShareCancel: () => void;
  onShareAccept: () => void;
  onDeletionStart: () => void;
  onDeletionCancel: () => void;
  onDeletionAccept: () => void;
  selectedInspections: ProductionSiteInspection[];
  hasInspections: boolean;
}
export const LocationActions = ({
  field,
  onShareStart,
  onShareCancel,
  onShareAccept,
  onDeletionStart,
  onDeletionCancel,
  onDeletionAccept,
  isInSharingFlow,
  selectedInspections,
  hasInspections,
  isInDeletionFlow,
}: LAProps) => {
  const org = useContext(ctxOrg);
  const profile = useContext(ctxProfile);
  const { networkEnabled } = applicationStateObservable.getValue();
  const { buildSharedFieldReportUrl: buildSharedLocationReportUrl } =
    useLocationUtils();

  const { productionType, locationId, name } = field;

  const { sharedTo } = useGetFieldReportShares(firestore, locationId);

  const [showShares, setShowShares] = useState<boolean>(false);

  const canCreateInspection = userHasPermission(profile, 'WRITE', 'ASSESSMENT');
  const canShareReport =
    userHasPermission(profile, 'SHARE_EXT', 'ASSESSMENT') && hasInspections;
  const canDeleteInspections =
    userHasPermission(profile, 'WRITE', 'ASSESSMENT') && hasInspections;

  const getNewInspectionLink = () => {
    const reference: ProductionSiteInspectionReference = {
      locationId,
      productionType,
      type: InspectionType.PreHarvest,
      date: new Date().getTime().toString(),
    };
    return buildProdSiteInspectionPath(org.id, reference);
  };

  const shareButtonText =
    selectedInspections.length > 0
      ? `share ${selectedInspections.length} inspection${
          selectedInspections.length > 1 ? 's' : ''
        }`
      : 'Select inspections';

  const deleteButtonText =
    selectedInspections.length > 0
      ? `delete ${selectedInspections.length} inspection${
          selectedInspections.length > 1 ? 's' : ''
        }`
      : 'Select inspections';

  return (
    <div slot="fixed" className="button-container">
      {isInSharingFlow ? (
        <>
          <IonButton
            disabled={selectedInspections.length === 0 || !networkEnabled}
            expand="block"
            color="tertiary"
            onClick={onShareAccept}
          >
            <IonIcon
              icon={
                !networkEnabled
                  ? cloudOfflineOutline
                  : selectedInspections.length > 0
                  ? shareOutline
                  : caretBackOutline
              }
            ></IonIcon>
            &nbsp;{shareButtonText}
          </IonButton>
          <IonButton expand="block" color="tertiary" onClick={onShareCancel}>
            <IonIcon icon={closeOutline}></IonIcon>
            &nbsp; cancel
          </IonButton>
        </>
      ) : isInDeletionFlow ? (
        <>
          <IonButton
            disabled={selectedInspections.length === 0 || !networkEnabled}
            expand="block"
            color="danger"
            onClick={onDeletionAccept}
          >
            <IonIcon
              icon={
                !networkEnabled
                  ? cloudOfflineOutline
                  : selectedInspections.length > 0
                  ? trashBinOutline
                  : caretBackOutline
              }
            ></IonIcon>
            &nbsp;{deleteButtonText}
          </IonButton>
          <IonButton expand="block" color="tertiary" onClick={onDeletionCancel}>
            <IonIcon icon={closeOutline}></IonIcon>
            &nbsp; cancel
          </IonButton>
        </>
      ) : (
        <>
          {sharedTo.length > 0 && (
            <IonButton
              expand="block"
              color="primary"
              onClick={() => setShowShares(true)}
            >
              View shares &nbsp;<IonBadge color="danger">{sharedTo.length}</IonBadge>
            </IonButton>
          )}
          {canShareReport && (
            <IonButton expand="block" color="tertiary" onClick={onShareStart}>
              <IonIcon icon={shareOutline}></IonIcon>
              &nbsp; share report
            </IonButton>
          )}
          {canCreateInspection && (
            <IonButton
              expand="block"
              color="tertiary"
              routerLink={getNewInspectionLink()}
            >
              <IonIcon icon={addCircleOutline}></IonIcon>
              &nbsp; new inspection
            </IonButton>
          )}
          {showShares && (
            <IonModal isOpen={showShares} onDidDismiss={() => setShowShares(false)}>
              <CSharedHistory
                onCancel={() => setShowShares(false)}
                name={name}
                buildLink={buildSharedLocationReportUrl}
                sharedTo={sharedTo}
              />
            </IonModal>
          )}
          {canDeleteInspections && (
            <IonButton
              expand="block"
              color="danger"
              fill="solid"
              onClick={onDeletionStart}
            >
              <IonIcon icon={trashBinOutline}></IonIcon>
              &nbsp; Delete inspections
            </IonButton>
          )}
        </>
      )}
    </div>
  );
};
